import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code,
  Copy,
  GearSix,
  Play,
  Trash
} from '@phosphor-icons/react';

/**
 * NodeContextMenu - Right-click context menu for flow nodes
 * 
 * Options:
 * - Test Node: Run the node in isolation
 * - Configure: Open node configuration panel
 * - Duplicate: Create a copy of the node
 * - Trash: Remove the node from flow
 */
const NodeContextMenu = ({ 
  isOpen, 
  position, 
  node, 
  onClose,
  onTest,
  onConfigure,
  onDuplicate,
  onDelete,
  readOnly = false
}) => {
  if (!isOpen || !node) return null;

  const menuItems = [
    {
      id: 'test',
      label: 'Test Node',
      icon: Play,
      iconColor: 'text-emerald-400',
      onClick: () => {
        onTest?.(node);
        onClose();
      },
      disabled: false
    },
    {
      id: 'configure',
      label: 'Configure',
      icon: GearSix,
      iconColor: 'text-indigo-400',
      onClick: () => {
        onConfigure?.(node);
        onClose();
      },
      disabled: readOnly
    },
    { type: 'divider' },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: Copy,
      iconColor: 'text-slate-400',
      onClick: () => {
        onDuplicate?.(node);
        onClose();
      },
      disabled: readOnly
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: Trash,
      iconColor: 'text-red-400',
      onClick: () => {
        onDelete?.(node);
        onClose();
      },
      disabled: readOnly
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Invisible overlay to catch clicks outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={onClose}
          />
          
          {/* Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            className="fixed z-50 min-w-[180px] py-1 bg-[var(--surface-1)] border border-[var(--border-default)] 
              rounded-[10px] shadow-xl overflow-hidden"
            style={{
              left: position.x,
              top: position.y,
              // Ensure menu doesn't go off-screen
              maxWidth: 'calc(100vw - 20px)',
              transform: position.x > window.innerWidth - 200 ? 'translateX(-100%)' : undefined
            }}
          >
            {/* Node Info Header */}
            <div className="px-3 py-2 border-b border-[var(--border-default)]">
              <div className="flex items-center gap-2">
                <Code size={12} className="text-[var(--text-muted)]" />
                <span className="text-xs text-[var(--text-secondary)] truncate max-w-[140px]">
                  {node.data?.label || node.id}
                </span>
              </div>
            </div>

            {/* Menu Items */}
            {menuItems.map((item, index) => {
              if (item.type === 'divider') {
                return <div key={index} className="my-1 border-t border-[var(--border-default)]" />;
              }

              return (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 text-sm text-left
                    transition-colors
                    ${item.disabled 
                      ? 'text-[var(--text-muted)] cursor-not-allowed' 
                      : 'text-[var(--text-secondary)] hover:bg-[var(--surface-3)] hover:text-white'
                    }
                  `}
                >
                  <item.icon size={16} className={item.disabled ? 'text-[var(--text-muted)]' : item.iconColor} />
                  {item.label}
                </button>
              );
            })}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NodeContextMenu;
